import React, { useEffect, useState } from "react";
import PageLayout from "@/components/layout/PageLayout";
import { Hero, Section, SectionHeader } from "@/components/layout/Sections";
import { Link } from "react-router-dom";
import { ArrowRight, Cpu, Award, Feather, Sparkles, Shield, Trophy, Zap, Store, Wrench, Package } from "lucide-react";
import { motion } from "framer-motion";
import api, { mediaUrl } from "@/lib/api";

const BRANDS = {
  ktm: {
    name: "KTM",
    slogan: (
      <>
        KTM<br />Ready to Race
      </>
    ),
    logo: "/logos/ktm.png",
    hero: "/showroom/ktm-showroom.jpg",
    accentClass: "text-[color:var(--nsa-orange)]",
    accentBg: "#FF6600",
    heroBody:
      "Il mondo KTM rappresenta prestazioni, adrenalina e innovazione. Dalle naked Duke alle Adventure, passando per le leggendarie EXC da enduro, ogni moto nasce con un unico obiettivo: offrire la massima esperienza di guida.",
    whyTitle: "Perché scegliere KTM",
    why: [
      { icon: Zap, title: "Performance", body: "Motori potenti, ciclistica raffinata e componentistica di alto livello." },
      { icon: Cpu, title: "Tecnologia", body: "Elettronica avanzata, riding mode, ABS Cornering e sistemi di assistenza alla guida." },
      { icon: Trophy, title: "Ready to Race", body: "Una filosofia nata nelle competizioni e trasferita sulle moto di serie." },
    ],
    rangesTitle: "Le gamme KTM",
    ranges: [
      { title: "DUKE", body: "Naked cattive, dalla 125 alla Super Duke." },
      { title: "ADVENTURE", body: "Ovunque tu voglia andare. Anche oltre." },
      { title: "ENDURO EXC", body: "Le regine dell'enduro." },
      { title: "MOTOCROSS SX", body: "Prestazioni factory su ogni pista." },
      { title: "CROSS COUNTRY XC", body: "Off-road puro, resistenza e prestazioni." },
      { title: "SUPERMOTO", body: "L'anima motard di KTM." },
      { title: "RC", body: "Sportive nate per la pista." },
    ],
    powerTitle: "PowerParts & PowerWear",
    powerBody: "Accessori originali e abbigliamento tecnico KTM. Personalizza la tua moto ed equipaggiati con il meglio della gamma ufficiale.",
    ctaTitle: "Vieni a scoprire il mondo KTM da NSA Motors",
    catalogUrl: "https://www.ktm.com/it-it/models.html",
  },
  husqvarna: {
    name: "Husqvarna Motorcycles",
    slogan: (
      <>
        Husqvarna<br />Motorcycles
      </>
    ),
    logo: "/logos/husqvarna.png",
    hero: "/showroom/husqvarna-enduro.webp",
    accentClass: "text-blue-900",
    accentBg: "#1e3a8a",
    heroBody: "",
    whyTitle: "Perché Husqvarna Motorcycles",
    why: [
      { icon: Feather, title: "Design", body: "Linee essenziali e stile inconfondibile." },
      { icon: Cpu, title: "Tecnologia", body: "Materiali premium, elettronica evoluta e massima qualità costruttiva." },
      { icon: Award, title: "Esperienza di guida", body: "Equilibrio perfetto tra comfort, controllo e prestazioni." },
    ],
    rangesTitle: "Le gamme Husqvarna",
    ranges: [
      { title: "Vitpilen", body: "Café racer moderna, urbana e affilata." },
      { title: "Svartpilen", body: "Neo-retrò dallo spirito street tracker." },
      { title: "Norden", body: "Adventure premium con anima urbana." },
      { title: "Enduro", body: "FE — off-road puro con firma svedese." },
      { title: "Motocross", body: "TC & FC per il vertice del racing." },
      { title: "Cross Country", body: "Endurance e velocità su ogni terreno." },
      { title: "Supermoto", body: "SM — motard con DNA racing." },
    ],
    powerTitle: "Technical Accessories & Apparel",
    powerBody: "Accessori tecnici originali Husqvarna e abbigliamento ufficiale: personalizza la tua moto ed equipaggiati per ogni tipo di guida.",
    ctaTitle: "Scopri la gamma Husqvarna da NSA Motors",
    catalogUrl: "https://www.husqvarna-motorcycles.com/it-it/models.html",
  },
  gasgas: {
    name: "GASGAS Trial",
    slogan: "GasGas Trial",
    logo: "/logos/gasgas.png",
    hero: "/showroom/gasgas-trial.webp",
    accentClass: "text-red-600",
    accentBg: "#dc2626",
    heroBody:
      "Il trial rappresenta la massima espressione della tecnica di guida. GASGAS sviluppa moto leggere, precise e performanti, progettate per affrontare ogni ostacolo con il massimo controllo.",
    whyTitle: "Perché scegliere GASGAS Trial",
    why: [
      { icon: Feather, title: "Leggerezza", body: "Massima agilità su ogni sezione." },
      { icon: Sparkles, title: "Precisione", body: "Controllo assoluto, in ogni condizione." },
      { icon: Trophy, title: "Competizione", body: "Tecnologia sviluppata nel Mondiale Trial." },
    ],
    rangesTitle: "Le gamme GASGAS Trial",
    ranges: [
      { title: "TXT Racing", body: "L'anima trial del marchio, la linea corse." },
      { title: "TXT GP", body: "Il top di gamma per la competizione al vertice." },
    ],
    powerTitle: "Accessori originali",
    powerBody: "Accessori GASGAS originali per la manutenzione e la personalizzazione della tua trial.",
    ctaTitle: "Scopri il mondo GASGAS Trial da NSA Motors",
    catalogUrl: "https://www.gasgas.com/it-it/models.html",
  },
};

export default function BrandPage({ brand }) {
  const b = BRANDS[brand];
  const [news, setNews] = useState([]);
  useEffect(() => {
    api.get(`/news?category=${brand}`).then((r) => setNews(r.data || [])).catch(() => {});
  }, [brand]);
  return (
    <PageLayout>
      <Hero
        image={b.hero}
        title={b.slogan}
        cta={{ label: "SCOPRI MODELLI", href: b.catalogUrl, external: true, color: b.accentBg }}
      />

      {/* Foto full-bleed decorativa sotto l'Hero (opzionale per marchio) */}
      {b.heroPhotoBelow && (
        <section className="w-full" data-testid={`${brand}-hero-below-photo`}>
          <div
            className="aspect-[21/9] md:aspect-[21/7] bg-cover bg-center"
            style={{ backgroundImage: `url(${b.heroPhotoBelow})` }}
          />
        </section>
      )}

      {/* Novità brand — banner orizzontale in evidenza subito dopo l'Hero */}
      {news.length > 0 && (
        <Section tone="gray">
          <div className="mb-6 flex items-center gap-3">
            <span className="inline-flex items-center gap-2 label-eyebrow text-white px-3 py-1.5 animate-pulse-glow" style={{ background: b.accentBg }}>
              <span className="h-2 w-2 rounded-full bg-white" /> Novità
            </span>
            <span className="label-eyebrow text-gray-500">Ultime notizie {b.name}</span>
          </div>
          {news.slice(0, 1).map((n) => (
            <Link
              key={n.id}
              to={`/moto/${brand}/news/${n.id}`}
              className="group grid md:grid-cols-5 gap-0 card-flat overflow-hidden bg-white hover:shadow-lg transition-shadow"
              data-testid={`brand-news-${n.id}`}
            >
              {n.cover_image && (
                <div
                  className="md:col-span-3 aspect-[16/9] md:aspect-auto md:min-h-[320px] bg-cover bg-center group-hover:scale-[1.02] transition-transform duration-500 origin-center"
                  style={{ backgroundImage: `url(${mediaUrl(n.cover_image)})` }}
                />
              )}
              <div className="md:col-span-2 p-6 md:p-10 flex flex-col justify-center">
                <span className="label-eyebrow text-gray-500">
                  {new Date(n.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
                </span>
                <h3 className="font-display uppercase text-2xl md:text-3xl mt-2 leading-tight">{n.title}</h3>
                <p className="text-gray-600 mt-4 leading-relaxed">{n.excerpt || (n.content || n.body || "").slice(0, 220)}</p>
                <span className={`mt-6 inline-flex items-center gap-2 label-eyebrow ${b.accentClass} link-underline`}>
                  Leggi l'articolo <ArrowRight size={14} />
                </span>
              </div>
            </Link>
          ))}
          {news.length > 1 && (
            <div className="mt-8 grid md:grid-cols-3 gap-6">
              {news.slice(1, 4).map((n) => (
                <Link
                  key={n.id}
                  to={`/moto/${brand}/news/${n.id}`}
                  className="card-flat overflow-hidden flex flex-col bg-white"
                  data-testid={`brand-news-mini-${n.id}`}
                >
                  {n.cover_image && (
                    <div className="aspect-[16/9] bg-cover bg-center" style={{ backgroundImage: `url(${mediaUrl(n.cover_image)})` }} />
                  )}
                  <div className="p-5 flex-1 flex flex-col">
                    <span className="label-eyebrow text-gray-500 text-[10px]">
                      {new Date(n.created_at).toLocaleDateString("it-IT")}
                    </span>
                    <h4 className="font-display uppercase text-lg mt-2 leading-tight">{n.title}</h4>
                    <p className="text-gray-600 text-sm mt-2 flex-1">{(n.excerpt || (n.content || n.body || "")).slice(0, 100)}…</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Logo + CTA */}
      <Section>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-8 mb-14">
          <img src={b.logo} alt={b.name} className="h-24 md:h-28 w-auto object-contain" data-testid={`${brand}-logo`} />
          <div className="flex-1">
            <span className={`label-eyebrow ${b.accentClass}`}>{b.name}</span>
            <h2 className="font-display uppercase tracking-tight text-4xl md:text-5xl mt-2">Scopri la gamma</h2>
            <p className="text-gray-600 mt-3 max-w-2xl">Catalogo aggiornato dal sito ufficiale {b.name}. Vieni a provare i modelli disponibili in showroom.</p>
          </div>
          <div className="flex flex-col gap-3">
            <a href={b.catalogUrl} target="_blank" rel="noopener noreferrer" className="bg-gray-900 text-white label-eyebrow px-6 py-4 inline-flex items-center gap-3 hover:bg-black" style={{ background: b.accentBg }}>
              SCOPRI LA GAMMA <ArrowRight size={16} />
            </a>
            <Link to="/contatti#form" className="border border-gray-900 text-gray-900 label-eyebrow px-6 py-4 inline-flex items-center gap-3 hover:bg-gray-900 hover:text-white">
              PRENOTA UN TEST RIDE
            </Link>
          </div>
        </div>
      </Section>

      {/* Perché scegliere */}
      <Section tone="gray">
        <SectionHeader eyebrow="I punti di forza" title={b.whyTitle} />
        <div className="grid md:grid-cols-3 gap-6">
          {b.why.map((w, i) => (
            <div key={i} className="card-flat p-8">
              <span className={b.accentClass}><w.icon size={28} /></span>
              <h3 className="font-display uppercase text-2xl mt-4">{w.title}</h3>
              <p className="mt-2 text-gray-600 leading-relaxed">{w.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Le gamme */}
      <Section>
        <SectionHeader eyebrow="Categorie" title={b.rangesTitle} description={`Le principali famiglie ${b.name}. Ogni categoria include più modelli — scopri il catalogo completo sul sito ufficiale.`} />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {b.ranges.map((r, i) => (
            <a
              key={i}
              href={b.catalogUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="card-flat p-6 group hover:border-gray-900"
              data-testid={`range-${brand}-${i}`}
            >
              <div className="h-1 w-10" style={{ background: b.accentBg }} />
              <h3 className="font-display uppercase text-xl md:text-2xl mt-4">{r.title}</h3>
              <p className="mt-2 text-sm text-gray-600">{r.body}</p>
              <span className="mt-4 inline-flex items-center gap-2 label-eyebrow text-gray-700 group-hover:text-gray-900">
                Scopri i modelli <ArrowRight size={14} />
              </span>
            </a>
          ))}
        </div>
      </Section>

      {/* PowerParts / Technical / Accessori */}
      <Section tone="gray">
        <div className="grid md:grid-cols-5 gap-10 items-center">
          <div className="md:col-span-3">
            <span className={`label-eyebrow ${b.accentClass}`}>Accessori & Abbigliamento</span>
            <h2 className="font-display uppercase text-4xl md:text-5xl mt-3">{b.powerTitle}</h2>
            <p className="mt-4 text-gray-700 leading-relaxed max-w-2xl">{b.powerBody}</p>
          </div>
          <div className="md:col-span-2 flex md:justify-end">
            <Link to="/contatti#form" className="border border-gray-900 text-gray-900 label-eyebrow px-6 py-4 inline-flex items-center gap-3 hover:bg-gray-900 hover:text-white">
              INFO ACCESSORI <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </Section>

      {/* Perché scegliere NSA Motors — sezione comune */}
      <Section>
        <SectionHeader eyebrow="I nostri servizi" title="Perché scegliere NSA Motors" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <ServiceCard icon={Store} title="Concessionaria Ufficiale" body="Vendita dei marchi ufficiali con consulenza specializzata." />
          <ServiceCard icon={Wrench} title="Officina Specializzata" body="Tecnici qualificati e assistenza certificata." />
          <ServiceCard icon={Package} title="Ricambi Originali" body="Disponibilità di ricambi e accessori originali." />
          <ServiceCard icon={Shield} title="Test Ride" body="Prenota una prova su strada sui modelli disponibili." />
        </div>
      </Section>

      {/* Novità brand (rimossa — spostata subito dopo l'Hero) */}

      {/* CTA finale */}
      <Section tone="dark">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <div>
            <span className={`label-eyebrow`} style={{ color: b.accentBg }}>{b.name}</span>
            <h2 className="font-display uppercase text-4xl md:text-5xl mt-3">{b.ctaTitle}</h2>
          </div>
          <div className="flex flex-col md:flex-row gap-3">
            <Link to="/contatti#form" className="bg-white text-gray-900 label-eyebrow px-6 py-4 inline-flex items-center gap-3 hover:bg-white/90">
              CONTATTACI <ArrowRight size={16} />
            </Link>
            <Link to="/contatti#form" className="border border-white/40 text-white label-eyebrow px-6 py-4 inline-flex items-center gap-3 hover:bg-white/10">
              PRENOTA TEST RIDE
            </Link>
          </div>
        </div>
      </Section>
    </PageLayout>
  );
}

function ServiceCard({ icon: Icon, title, body }) {
  return (
    <div className="card-flat p-6">
      <span className="text-[color:var(--nsa-orange)]"><Icon size={26} /></span>
      <h4 className="font-display uppercase text-lg mt-4">{title}</h4>
      <p className="mt-2 text-sm text-gray-600 leading-relaxed">{body}</p>
    </div>
  );
}
